import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./product-form.component.scoped.scss";
import {
  Box,
  TextField,
  Typography,
  Button,
  FormControl,
  Select,
  MenuItem,
  makeStyles,
} from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import { useTranslation } from "react-i18next";
import { ProductsService } from "../../../../../../services/products.services";
import { LangService } from "../../../../../../services/lang/lang.service";
import { CheckIcon } from "@mantine/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { urlRegex } from "../../../../../../statics/validators";
import { showPopupToastMsg, showToastMsg } from "../../../../../../helpers";
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from "@material-ui/pickers";
import EventOutlinedIcon from "@material-ui/icons/EventOutlined";
import MomentUtils from "@date-io/moment";
import moment from "moment";
import { ApplicationTypes } from "../../../../types/application-types";

interface ProductFormComponentProps {
  loading: boolean;
  product: any;
  onUpdate: () => void;
}

const useStyles = makeStyles(() => ({
  datePackerInputRoot: {
    backgroundColor: "var(--light)",
    height: "100%",
    paddingTop: 0,
    margin: "0 !important",
  },
  datePackerInputAdornment: {
    margin: "0 !important",
  },
}));

const ProductFormComponent: React.FC<ProductFormComponentProps> = ({
  loading,
  product,
  onUpdate,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const productsService = useMemo(() => new ProductsService(), []);
  const langService = useMemo(() => new LangService(), []);
  const locale = useMemo(() => langService.getCurrentLang(), [langService]);

  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({ link: "" });
  const [license_validity, setLicense_validity]: any = useState(null);

  useEffect(() => {
    moment.locale(locale);
  }, [locale]);

  const [formData, setFormData] = useState({
    name: "",
    vendor: "",
    usage: "",
    version: "",
    version_comments: "",
    version_details: "",
    type: "",
    environment: "",
    link: "",
    changes: "",
    license_type: "",
    license_validity: "",
  });

  // Pre-fill form when product data is available
  useEffect(() => {
    if (product && product.id) {
      const licenseValidity = product.licenseValidity || "";

      setFormData({
        name: product.name || "",
        vendor: product.vendor || "",
        usage: product.usage || "",
        version: product.version || "",
        version_comments: product.versionComments || "",
        version_details: product.versionDetails || "",
        type: product.type || "",
        environment: product.environment || "",
        link: product.link || "",
        changes: product.changes || "",
        license_type: product.licenseType || "",
        license_validity: licenseValidity,
      });

      // Set license validity date if available
      if (licenseValidity) {
        const licenseDate = moment(licenseValidity);
        if (licenseDate.isValid()) {
          setLicense_validity(licenseDate);
        } else {
          setLicense_validity(null);
        }
      } else {
        setLicense_validity(null);
      }
    }
  }, [product]);

  /**
   * Check if form is valid
   */
  const isFormValid = useMemo(() => {
    const hasErrors = Object.values(errors).some((error) => error !== "");

    // Required fields - make some optional for updates
    const requiredFieldsValid =
      formData.name.trim() !== "" &&
      formData.vendor.trim() !== "" &&
      formData.usage.trim() !== "" &&
      formData.version.trim() !== "" &&
      formData.version_details.trim() !== "" &&
      formData.type !== "" &&
      formData.environment.trim() !== "" &&
      formData.license_type.trim() !== "";

    // License validity is optional or can be set via date picker
    const licenseValid =
      formData.license_validity.trim() !== "" || license_validity !== null;

    return !hasErrors && requiredFieldsValid && licenseValid;
  }, [formData, errors, license_validity]);

  const validateLink = (value: string) =>
    urlRegex.test(value) ? "" : "Invalid link format";

  const handleChange = (e: any, isDate?: Boolean) => {
    if (isDate) {
      setLicense_validity(e);
      const formattedDate = e ? moment(e).format("YYYY-MM-DD") : "";
      setFormData((prev) => ({
        ...prev,
        license_validity: formattedDate,
      }));
    } else {
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name]: value }));
      if (name === "link") {
        setErrors((prev) => ({ ...prev, link: validateLink(value) }));
      }
    }
  };

  const handleUpdate = useCallback(async () => {
    setSaving(true);
    try {
      formData.license_validity = moment(license_validity).format("YYYY-MM-DD");
      const response = await productsService.update(product.id, formData);
      if (response && response.message === "SUCCESS") {
        showToastMsg(
          "success",
          t("PRODUCTS_PAGE.PRODUCT_UPDATED_SUCCESSFULLY"),
        );
        // Optionally refresh the product data
        if (onUpdate) {
          onUpdate();
        }
      } else {
        showPopupToastMsg("error", t("SOME_THING_WENT_WRONG"));
      }
    } catch (error) {
      console.error("Error updating product:", error);
      showPopupToastMsg("error", t("SOME_THING_WENT_WRONG"));
    } finally {
      setSaving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product.id, formData, productsService, t, onUpdate]);

  return (
    <Box
      className="global__box__content product__details__content__form p-0"
      style={{ height: "fit-content" }}
    >
      <Box className="global__box__content__header">
        <Typography
          className="text-capitalize"
          variant="h3"
          color="textPrimary"
        >
          {loading ? (
            <Skeleton variant="text" width={150} height={20} />
          ) : (
            <>{t("PRODUCTS_PAGE.AI_PRODUCT_DETAILS")}</>
          )}
        </Typography>
      </Box>
      <Box className="global__box__content__body">
        {/* PRODUCT NAME */}
        <Box className="form__item">
          <Typography
            variant="subtitle2"
            color="textPrimary"
            className="text-capitalize"
          >
            {t("PRODUCTS_PAGE.PRODUCT_NAME")}
          </Typography>
          <Box className="standard_text_input">
            <TextField
              className="w-100"
              placeholder={t("PRODUCTS_PAGE.PRODUCT_NAME")}
              onChange={handleChange}
              value={formData.name}
              name="name"
              variant="standard"
              inputProps={{
                maxLength: 30,
              }}
            />
            <Typography
              variant="subtitle2"
              className="mt-2"
              color="textSecondary"
              style={{ fontWeight: 300 }}
            >
              {t("PRODUCTS_PAGE.NAME_OF_PRODUCT_HELP_TEXT")}
            </Typography>
          </Box>
        </Box>

        {/* VENDOR NAME */}
        <Box className="form__item">
          <Typography
            variant="subtitle2"
            color="textPrimary"
            className="text-capitalize"
          >
            {t("PRODUCTS_PAGE.VENDOR_NAME")}
          </Typography>
          <Box className="standard_text_input">
            <TextField
              className="w-100"
              placeholder={t("PRODUCTS_PAGE.VENDOR_NAME")}
              onChange={handleChange}
              value={formData.vendor}
              name="vendor"
              variant="standard"
              inputProps={{
                maxLength: 30,
              }}
            />
            <Typography
              variant="subtitle2"
              className="mt-2"
              color="textSecondary"
              style={{ fontWeight: 300 }}
            >
              {t("PRODUCTS_PAGE.VENDOR_OF_PRODUCT_HELP_TEXT")}
            </Typography>
          </Box>
        </Box>

        {/* PRODUCT USAGE */}
        <Box className="form__item">
          <Typography
            variant="subtitle2"
            color="textPrimary"
            className="text-capitalize"
          >
            {t("PRODUCTS_PAGE.USAGE")}
          </Typography>
          <Box className="standard_text_input">
            <TextField
              className="w-100 text-area-multiline"
              placeholder={t("PRODUCTS_PAGE.PRODUCT_USAGE")}
              onChange={handleChange}
              value={formData.usage}
              name="usage"
              variant="standard"
              multiline
              rows={7}
              inputProps={{
                maxLength: 500,
              }}
            />
            <Typography
              variant="subtitle2"
              className="mt-2"
              color="textSecondary"
              style={{ fontWeight: 300 }}
            >
              {t("PRODUCTS_PAGE.DESCRIBE_YOUR_USAGE_OF_PRODUCT_HELP_TEXT")}
            </Typography>
          </Box>
        </Box>

        {/* ENVIRONMENT */}
        <Box className="form__item">
          <Typography
            variant="subtitle2"
            color="textPrimary"
            className="text-capitalize"
          >
            {t("PRODUCTS_PAGE.HARDWARE_ENVIRONMENT")}
          </Typography>
          <Box className="standard_text_input">
            <TextField
              className="w-100"
              placeholder={t("PRODUCTS_PAGE.HARDWARE_ENVIRONMENT")}
              onChange={handleChange}
              value={formData.environment}
              name="environment"
              variant="standard"
              inputProps={{
                maxLength: 50,
              }}
            />
            <Typography
              variant="subtitle2"
              className="mt-2"
              color="textSecondary"
              style={{ fontWeight: 300 }}
            >
              {t("PRODUCTS_PAGE.HARDWARE_USED_OF_PRODUCT_HELP_TEXT")}
            </Typography>
          </Box>
        </Box>

        {/* APPLICATION TYPE */}
        <Box className="form__item">
          <Typography
            variant="subtitle2"
            color="textPrimary"
            className="text-capitalize"
          >
            {t("PRODUCTS_PAGE.APPLICATION_TYPE")}
          </Typography>
          <Box className="standard_text_input">
            <FormControl className="select-input select-grey w-100">
              <Select
                name="type"
                onChange={(e: any) => handleChange(e)}
                IconComponent={ExpandMoreIcon}
                value={formData.type || ""}
                displayEmpty
              >
                <MenuItem value="" disabled>
                  {t("PRODUCTS_PAGE.APPLICATION_TYPE")}
                </MenuItem>
                {ApplicationTypes.map((value: any, index: number) => {
                  return (
                    <MenuItem key={index} value={value.id}>
                      {t(value.name)}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            <Typography
              variant="subtitle2"
              className="mt-2"
              color="textSecondary"
              style={{ fontWeight: 300 }}
            >
              {t("PRODUCTS_PAGE.TYPE_OF_PRODUCT_HELP_TEXT")}
            </Typography>
          </Box>
        </Box>

        {/* PRODUCT VERSION */}
        <Box className="form__item">
          <Typography
            variant="subtitle2"
            color="textPrimary"
            className="text-capitalize"
          >
            {t("PRODUCTS_PAGE.PRODUCT_VERSION")}
          </Typography>
          <Box className="standard_text_input">
            <TextField
              className="w-100"
              placeholder={t("PRODUCTS_PAGE.PRODUCT_VERSION")}
              onChange={handleChange}
              value={formData.version}
              name="version"
              variant="standard"
              inputProps={{
                maxLength: 10,
              }}
            />
            <Typography
              variant="subtitle2"
              className="mt-2"
              color="textSecondary"
              style={{ fontWeight: 300 }}
            >
              {t("PRODUCTS_PAGE.VERSION_OF_PRODUCT_HELP_TEXT")}
            </Typography>
          </Box>
        </Box>

        {/* VERSION DETAILS */}
        <Box className="form__item">
          <Typography
            variant="subtitle2"
            color="textPrimary"
            className="text-capitalize"
          >
            {t("PRODUCTS_PAGE.VERSION_DETAILS")}
          </Typography>
          <Box className="standard_text_input">
            <TextField
              className="w-100 text-area-multiline"
              placeholder={t("PRODUCTS_PAGE.VERSION_DETAILS")}
              onChange={handleChange}
              value={formData.version_details}
              name="version_details"
              variant="standard"
              multiline
              rows={7}
              inputProps={{
                maxLength: 500,
              }}
            />
            <Typography
              variant="subtitle2"
              className="mt-2"
              color="textSecondary"
              style={{ fontWeight: 300 }}
            >
              {t("PRODUCTS_PAGE.VERSION_DETAILS_OF_PRODUCT_HELP_TEXT")}
            </Typography>
          </Box>
        </Box>

        {/* INDIVIDUAL CHANGES */}
        <Box className="form__item">
          <Typography
            variant="subtitle2"
            color="textPrimary"
            className="text-capitalize"
          >
            {t("PRODUCTS_PAGE.INDIVIDUAL_CHANGES")}
          </Typography>
          <Box className="standard_text_input">
            <TextField
              className="w-100 text-area-multiline"
              placeholder={t("PRODUCTS_PAGE.INDIVIDUAL_CHANGES")}
              onChange={handleChange}
              value={formData.changes}
              name="changes"
              variant="standard"
              multiline
              rows={7}
              inputProps={{
                maxLength: 500,
              }}
            />
            <Typography
              variant="subtitle2"
              className="mt-2"
              color="textSecondary"
              style={{ fontWeight: 300 }}
            >
              {t("PRODUCTS_PAGE.COMPANY_SPECIFIC_CHANGES_OF_PRODUCT_HELP_TEXT")}
            </Typography>
          </Box>
        </Box>

        {/* LINK */}
        <Box className="form__item">
          <Typography
            variant="subtitle2"
            color="textPrimary"
            className="text-capitalize"
          >
            {t("PRODUCTS_PAGE.LINK")}
          </Typography>
          <Box className="standard_text_input">
            <TextField
              className="w-100"
              placeholder={t("PRODUCTS_PAGE.LINK")}
              onChange={handleChange}
              value={formData.link}
              name="link"
              variant="standard"
            />
            {errors.link && <span className="error-msg">{errors.link}</span>}
            <Typography
              variant="subtitle2"
              className="mt-2"
              color="textSecondary"
              style={{ fontWeight: 300 }}
            >
              {t("PRODUCTS_PAGE.LINK_OF_PRODUCT_HELP_TEXT")}
            </Typography>
          </Box>
        </Box>

        {/* VERSION COMMENT */}
        <Box className="form__item">
          <Typography
            variant="subtitle2"
            color="textPrimary"
            className="text-capitalize"
          >
            {t("PRODUCTS_PAGE.VERSION_COMMENT")}
          </Typography>
          <Box className="standard_text_input">
            <TextField
              className="w-100"
              placeholder={t("PRODUCTS_PAGE.VERSION_COMMENT")}
              onChange={handleChange}
              value={formData.version_comments}
              name="version_comments"
              variant="standard"
              inputProps={{
                maxLength: 150,
              }}
            />
            <Typography
              variant="subtitle2"
              className="mt-2"
              color="textSecondary"
              style={{ fontWeight: 300 }}
            >
              {t("PRODUCTS_PAGE.VERSION_COMMENT_OF_PRODUCT_HELP_TEXT")}
            </Typography>
          </Box>
        </Box>

        {/* LICENSE TYPE */}
        <Box className="form__item">
          <Typography
            variant="subtitle2"
            color="textPrimary"
            className="text-capitalize"
          >
            {t("PRODUCTS_PAGE.LICENSE_TYPE")}
          </Typography>
          <Box className="standard_text_input">
            <TextField
              className="w-100"
              placeholder={t("PRODUCTS_PAGE.LICENSE_TYPE")}
              onChange={handleChange}
              value={formData.license_type}
              name="license_type"
              variant="standard"
              inputProps={{
                maxLength: 30,
              }}
            />
            <Typography
              variant="subtitle2"
              className="mt-2"
              color="textSecondary"
              style={{ fontWeight: 300 }}
            >
              {t("PRODUCTS_PAGE.TYPE_OF_LICENSE_OF_PRODUCT_HELP_TEXT")}
            </Typography>
          </Box>
        </Box>

        {/* LICENSE VALIDITY */}
        <Box className="form__item">
          <Typography
            variant="subtitle2"
            color="textPrimary"
            className="text-capitalize"
          >
            {t("PRODUCTS_PAGE.LICENSE_VALIDITY")}
          </Typography>
          <Box className="standard_text_input">
            <MuiPickersUtilsProvider
              utils={MomentUtils}
              libInstance={moment}
              locale={locale}
            >
              <KeyboardDatePicker
                disableToolbar
                variant="inline"
                format="DD/MM/YYYY"
                autoOk
                value={license_validity}
                onChange={(e: any) => handleChange(e, true)}
                name="license_validity"
                className="date-picker-standard w-100"
                placeholder={t("PRODUCTS_PAGE.LICENSE_VALIDITY")}
                keyboardIcon={<EventOutlinedIcon />}
                inputVariant="standard"
                InputProps={{
                  classes: { root: classes.datePackerInputRoot },
                }}
                InputLabelProps={{
                  style: { zIndex: 1 },
                }}
                InputAdornmentProps={{
                  classes: { root: classes.datePackerInputAdornment },
                }}
              />
            </MuiPickersUtilsProvider>
          </Box>
        </Box>

        {/* UPDATE BUTTON */}
        <Box className="d-flex justify-content-end mt-4">
          <Button
            disabled={!isFormValid || saving || Boolean(errors.link)}
            onClick={handleUpdate}
            className="btn-primary-theme"
            variant="contained"
            color="primary"
          >
            <CheckIcon /> {t("UPDATE")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default ProductFormComponent;
